import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from "react-router-dom";
import "./App.css";
import ControlLayout from "./operation/ControlLayout";
import ManagementLayout from "./management/ManagementLayout";
import MaintenanceLayout from "./maintenance/MaintenanceLayout"; // Being repurposed as LinkageLayout
import RepairLayout from "./repair/RepairLayout";
import { db, Vehicle, Driver } from "./data/database"; // database.ts에서 db 객체와 타입 import
import LandingFrame from "./landing/LandingFrame";
import LoginView from "./login/LoginView";
import HomeView from "./home/HomeView";
import AdminView from "./admin/AdminView";
import PricingGuideOverlay from "./login/pricing/PricingGuideOverlay";

const getInitialAppState = (): "landing" | "login" | "main" =>
  new URLSearchParams(window.location.search).get("view") === "login" ? "login" : "landing";

const App: React.FC = () => {
  const [appState, setAppState] = useState<"landing" | "login" | "main">(getInitialAppState);

  // 개편안 프로토타입(/eview/)은 이 앱과 다른 번들이라 React 상태를 공유하지 못한다.
  // 같은 도메인이므로 localStorage 로 로그인 여부만 넘겨 준다 — 로그인 없이 URL 로
  // 바로 들어오면 프로토타입이 이 표식을 보고 데모 첫 화면으로 돌려보낸다.
  // 실제 차단이 아니라 동선 정리다. 외부 노출이 필요해지면 nginx Basic 인증을 건다.
  const PROTO_KEY = "eview-demo-auth";
  const enterMain = () => { try { localStorage.setItem(PROTO_KEY, "1"); } catch {} setAppState("main"); };
  const leaveMain = () => { try { localStorage.removeItem(PROTO_KEY); } catch {} setAppState("login"); };
  const [isLoading, setIsLoading] = useState(true);
  const [showPricing, setShowPricing] = useState(false);

  // 중앙 데이터 상태
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);

  // 컴포넌트 마운트 시 DB에서 데이터 로드
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [vehicleData, driverData] = await Promise.all([
          db.fetchVehicles(),
          db.fetchDrivers(),
        ]);
        setVehicles(vehicleData);
        setDrivers(driverData);
      } catch (error) {
        console.error("데이터 로드 실패:", error);
        alert("데이터를 불러오는 데 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  if (appState === "landing") {
    return <LandingFrame />;
  }

  if (appState === "login") {
    return <LoginView onLogin={enterMain} />;
  }

  return (
    <Router>
      <div className="app-root">
        <header className="app-header">
          <div className="app-header-left">
            <div className="app-logo-area">
              <img src="/assets/images/logos/eview-logo.png" alt="EView" className="app-logo-image" style={{ height: "40px", width: "auto" }} />
            </div>
            <nav className="app-tabs">
              <NavLink to="/home" className={({ isActive }: { isActive: boolean }) => (isActive ? "tab active" : "tab")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                홈
              </NavLink>
              <NavLink to="/management" className={({ isActive }: { isActive: boolean }) => (isActive ? "tab active" : "tab")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
                관리
              </NavLink>
              <NavLink to="/control" className={({ isActive }: { isActive: boolean }) => (isActive ? "tab active" : "tab")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
                관제
              </NavLink>
              <NavLink to="/linkage" className={({ isActive }: { isActive: boolean }) => (isActive ? "tab active" : "tab")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                운영
              </NavLink>
              <NavLink to="/repair" className={({ isActive }: { isActive: boolean }) => (isActive ? "tab active" : "tab")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                정비
              </NavLink>
              <NavLink to="/report" className={({ isActive }: { isActive: boolean }) => (isActive ? "tab active" : "tab")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                분석
              </NavLink>
              {/* 개편안 프로토타입 — 별도 번들이라 라우터가 아닌 실제 링크다.
                  데모 상태를 잃지 않도록 새 탭으로 연다.
                  index.html 까지 적는 이유: nginx 의 demo.eview.im 블록이
                  `try_files $uri /index.html` 라서 `/eview/`(끝 슬래시)가 파일로 안 잡히고
                  이 앱의 index.html 로 폴백된다 — 주소는 /eview/ 인데 데모 랜딩이 뜬다.
                  nginx 를 `try_files $uri $uri/ /index.html` 로 고치면 /eview/ 로 줄여도 된다. */}
              <a href="/eview/index.html" target="_blank" rel="noopener noreferrer" className="tab tab-proto">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"></rect><path d="M3 9h18"></path><path d="M9 21V9"></path></svg>
                개편안
                <span className="tab-badge">NEW</span>
              </a>
            </nav>
          </div>
          <div className="app-header-right">
            <div className="partner-area">
              <span className="partner-logo-text" style={{ color: '#0369a1', fontWeight: 800, fontStyle: 'italic', fontSize: '18px' }}>ILYANG <span style={{ fontSize: '12px', fontWeight: 600 }}>일양로지스</span></span>
            </div>
            <div className="user-area">
              <span className="user-name" style={{ color: '#1e293b', fontSize: '15px' }}><strong>홍길동님</strong> (charles.lee@prediction.im)</span>
              <div className="user-actions">
                <button type="button" className="btn-small" onClick={() => setShowPricing(true)}>요금제</button>
                <NavLink to="/admin" className={({ isActive }) => `btn-small ${isActive ? 'active' : ''}`} style={{ textDecoration: 'none' }}>관리자</NavLink>
                <button type="button" className="btn-small">마이페이지</button>
                <button type="button" className="btn-small text-red" onClick={leaveMain}>로그아웃</button>
              </div>
            </div>
          </div>
        </header>

        <main className="app-main">
          {isLoading ? (
            <div className="loading-spinner">데이터를 불러오는 중입니다...</div>
          ) : (
            <Routes>
              <Route path="/home" element={
                <div className="page-wrapper">
                  <HomeView
                    vehicles={vehicles}
                    drivers={drivers}
                    onShowIntro={() => setAppState("landing")}
                    onShowPricing={() => setShowPricing(true)}
                  />
                </div>
              } />
              <Route path="/control/*" element={
                <div className="page-wrapper p-0" style={{ padding: 0 }}>
                  <ControlLayout vehicles={vehicles} />
                </div>
              } />
              <Route path="/management/*" element={
                <div className="page-wrapper p-0" style={{ padding: 0 }}>
                  <ManagementLayout
                    vehicles={vehicles}
                    setVehicles={setVehicles}
                    drivers={drivers}
                    setDrivers={setDrivers}
                  />
                </div>
              } />
              <Route path="/linkage/*" element={
                <div className="page-wrapper p-0" style={{ padding: 0 }}>
                  <MaintenanceLayout vehicles={vehicles} />
                </div>
              } />
              <Route path="/repair/*" element={
                <div className="page-wrapper p-0" style={{ padding: 0 }}>
                  <RepairLayout />
                </div>
              } />
              <Route path="/report" element={
                <div className="page-wrapper coming-soon-page">
                  <div className="cs-message">
                    <h2>🚀 해당 기능은 준비 중입니다.</h2>
                    <p>곧 더 나은 서비스로 찾아뵙겠습니다.</p>
                  </div>
                </div>
              } />
              <Route path="/admin/*" element={
                <div className="page-wrapper">
                  <AdminView />
                </div>
              } />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          )}
        </main>

        {showPricing && (
          <PricingGuideOverlay onClose={() => setShowPricing(false)} isLoggedIn={true} currentVehicleCount={vehicles.length} />
        )}
      </div>
    </Router>
  );
};

export default App;
